import { useState } from "react";
import { cn } from "../../utils/cn";

/**
 * A part-of-a-whole bar with a legend, both interactive.
 *
 *  - Hover a bar segment: a tooltip says what it is; its legend row (and the
 *    segment of a hovered row) light up, the rest dim.
 *  - Click a segment or a row: onSelect(key) — click the selected one again to
 *    clear it. Segments are real buttons, so they are reachable from the keyboard.
 *  - The legend is collapsed to the `visibleCount` largest rows plus whatever is
 *    selected, with "Show N more". The bar always draws EVERY segment, and a
 *    hidden one still explains itself on hover, so collapsing never hides data.
 *    Rows keep their fixed order whether collapsed or expanded, so nothing jumps.
 *
 * items: [{ key, label, value, color (a Tailwind bg class), selectable? }]
 */
export function StackedBreakdown({
  items,
  total,
  selected = "",
  onSelect,
  visibleCount = 2,
  formatValue = (n) => n.toLocaleString(),
  actionHint = "Show these in the list",
  className,
}) {
  const [hovered, setHovered] = useState("");
  const [expanded, setExpanded] = useState(false);

  const whole = total ?? items.reduce((s, i) => s + i.value, 0);
  const pct = (v) => (whole ? `${((v / whole) * 100).toFixed(1).replace(".", ",")}%` : "—");

  // The largest rows, then the selected one, whatever its size
  const top = new Set(
    [...items]
      .sort((a, b) => b.value - a.value)
      .slice(0, visibleCount)
      .map((i) => i.key),
  );
  const isShown = (i) => expanded || top.has(i.key) || i.key === selected;
  const shown = items.filter(isShown);
  const hiddenCount = items.length - shown.length;
  const canCollapse = items.length > visibleCount;

  const segments = items.filter((i) => i.value > 0);
  const focusKey = hovered || "";
  const dimmed = (key) => (focusKey && focusKey !== key) || (!focusKey && selected && selected !== key);

  // tooltip sits above the middle of the hovered segment
  let offset = 0;
  let tip = null;
  for (const seg of segments) {
    const width = whole ? (seg.value / whole) * 100 : 0;
    if (seg.key === hovered) tip = { seg, left: offset + width / 2 };
    offset += width;
  }

  const toggle = (item) => {
    if (item.selectable === false || !onSelect) return;
    onSelect(selected === item.key ? "" : item.key);
  };

  return (
    <div className={className}>
      <div className="relative mb-3" onMouseLeave={() => setHovered("")}>
        {tip && (
          <div
            role="tooltip"
            className="pointer-events-none absolute bottom-full mb-2 -translate-x-1/2 whitespace-nowrap rounded-md bg-slate-900 px-2.5 py-1.5 text-xs text-white shadow-lg z-10"
            style={{ left: `${Math.min(88, Math.max(12, tip.left))}%` }}
          >
            <span className="font-medium">{tip.seg.label}</span>
            <span className="text-slate-300"> · {formatValue(tip.seg.value)} · {pct(tip.seg.value)}</span>
          </div>
        )}
        <div className="flex gap-0.5 h-3 rounded-full overflow-hidden bg-slate-100">
          {segments.map((seg) => (
            <button
              key={seg.key}
              type="button"
              onMouseEnter={() => setHovered(seg.key)}
              onFocus={() => setHovered(seg.key)}
              onBlur={() => setHovered("")}
              onClick={() => toggle(seg)}
              aria-label={`${seg.label}: ${formatValue(seg.value)}, ${pct(seg.value)}`}
              aria-pressed={selected === seg.key}
              disabled={seg.selectable === false || !onSelect}
              className={cn(
                "h-full min-w-[4px] transition-opacity focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-900 focus-visible:ring-offset-1 rounded-[2px]",
                seg.color,
                dimmed(seg.key) ? "opacity-30" : "opacity-100",
                seg.selectable === false || !onSelect ? "cursor-default" : "cursor-pointer",
              )}
              style={{ flexGrow: seg.value, flexBasis: 0 }}
            />
          ))}
        </div>
      </div>

      <ul>
        {shown.map((item) => {
          const active = selected === item.key;
          const clickable = item.selectable !== false && !!onSelect;
          return (
            <li key={item.key}>
              <button
                type="button"
                onMouseEnter={() => setHovered(item.key)}
                onMouseLeave={() => setHovered("")}
                onFocus={() => setHovered(item.key)}
                onBlur={() => setHovered("")}
                onClick={() => toggle(item)}
                disabled={!clickable}
                aria-pressed={active}
                title={clickable ? (active ? "Showing these — click to clear" : actionHint) : undefined}
                className={cn(
                  "w-full flex items-center gap-2.5 px-2 -mx-2 py-1.5 rounded-md text-left transition-colors",
                  active ? "bg-slate-100" : hovered === item.key ? "bg-slate-50" : "",
                  clickable ? "cursor-pointer" : "cursor-default",
                )}
              >
                <span className={cn("h-2 w-2 rounded-full shrink-0", item.color)} />
                <span className="text-sm text-slate-700 flex-1 truncate">{item.label}</span>
                <span className={cn("text-sm tabular-nums text-right", item.value ? "text-slate-900 font-medium" : "text-slate-300")}>
                  {formatValue(item.value)}
                </span>
                <span className="text-xs text-slate-400 tabular-nums w-12 text-right">{item.value ? pct(item.value) : "—"}</span>
              </button>
            </li>
          );
        })}
      </ul>

      {canCollapse && (expanded || hiddenCount > 0) && (
        <button
          type="button"
          onClick={() => {
            setExpanded((v) => !v);
            // a row that vanishes under the pointer never fires mouseleave
            setHovered("");
          }}
          aria-expanded={expanded}
          className="mt-1 text-xs text-slate-500 hover:text-slate-800 underline underline-offset-2"
        >
          {expanded ? "Show less" : `Show ${hiddenCount} more`}
        </button>
      )}
    </div>
  );
}
